import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import * as XLSX from 'xlsx';
import PptxGenJS from 'pptxgenjs';
import './styles/TableComponent.css';

function TableComponent({ cols, statements, datavalue, lvl1, lvl2, lvl3, lvl4, flt3, flt4, displaySettings, summary=false, szr=false }) {
  const [showChart, setShowChart] = useState(false);
  const [isWeighted, setIsWeighted] = useState(false);
  const [isSigtest, setIsSigtest] = useState(false);

  const freqDecimals = displaySettings?.freqDecimals ?? 0;
  const pctDecimals  = displaySettings?.pctDecimals  ?? 1;
  const showUnweightedBase = displaySettings?.showUnweightedBase ?? true;
  const showWeightedBase   = displaySettings?.showWeightedBase   ?? false;

  const STAT_KEYS = {
    '-101': ['Mean',   'WeightedMean'],
    '-102': ['Median', 'WeightedMedian'],
    '-103': ['Std',    'WeightedStd'],
    '-104': ['SE',     'WeightedSE'],
    '-105': ['Mode',   'WeightedMode'],
    '-106': ['Min',    'WeightedMin'],
    '-107': ['Max',    'WeightedMax'],
    '-108': ['Q1',     'WeightedQ1'],
    '-109': ['Q3',     'WeightedQ3'],
  };

  const STAT_DECIMALS = {
    '-101': displaySettings?.meanDecimals   ?? 2,
    '-102': displaySettings?.medianDecimals ?? 2,
    '-103': displaySettings?.stdDecimals    ?? 2,
    '-104': displaySettings?.seDecimals     ?? 2,
    '-105': displaySettings?.modeDecimals   ?? 2,
    '-106': displaySettings?.minDecimals    ?? 2,
    '-107': displaySettings?.maxDecimals    ?? 2,
    '-108': displaySettings?.q1Decimals     ?? 2,
    '-109': displaySettings?.q3Decimals     ?? 2,
  };

  const fmtStat = (val, stIndex) => {
    if (val === undefined || val === null || val === '' || val === 'N/A') return '-';
    const n = parseFloat(val);
    const dec = STAT_DECIMALS[String(stIndex)] ?? 2;
    return isNaN(n) ? String(val) : n.toFixed(dec);
  };

  const fmtFreq = (val) => {
    if (val === undefined || val === null || val === '') return '-';
    const n = parseFloat(val);
    return isNaN(n) ? String(val) : n.toFixed(freqDecimals);
  };

  const fmtPct = (val) => {
    if (val === undefined || val === null || val === '') return '-';
    const s = String(val);
    const hasPct = s.includes('%');
    const n = parseFloat(s.replace('%', ''));
    if (isNaN(n)) return s;
    return hasPct ? n.toFixed(pctDecimals) + '%' : n.toFixed(pctDecimals);
  };

  const handleToggle = () => setShowChart(!showChart);
  const handleToggleWeighting = () => setIsWeighted(!isWeighted);
  const handleToggleSigtest = () => setIsSigtest(!isSigtest);

  const downloadExcel = () => {
    const workSheet = XLSX.utils.json_to_sheet(datavalue);
    const workBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workBook, workSheet, "Data");
    XLSX.writeFile(workBook, "data.xlsx");
  };

  const downloadPPT = () => {
    let pptx = new PptxGenJS();
    let slide = pptx.addSlide();
    slide.addChart(
      pptx.ChartType.bar, 
      chartData.datasets.map(ds => ({
        name: ds.label,
        labels: chartData.labels,
        values: ds.data
      }))
    );
    pptx.writeFile({ fileName: "chart.pptx" });
  };

  const chartData = {
    labels: statements.filter(st => st.index !== -98).map(st => st.label),
    datasets: cols.map((col, idx) => ({
      label: col.label,
      data: statements.filter(st => st.index !== -98).map(st => {
        const item = datavalue.find(
          d => d[lvl1] === col.index && 
               d[lvl2] === st.index && 
               d[lvl3] === flt3 && 
               d[lvl4] === flt4
        );
        return item && item.data.perc ? parseFloat(item.data.perc.replace('%', '')) : 0;
      }),
      backgroundColor: `hsla(${(idx * 360) / cols.length}, 70%, 60%, 0.7)`,
      borderColor: `hsla(${(idx * 360) / cols.length}, 70%, 50%, 1)`,
      borderWidth: 2,
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          padding: 15,
          font: {
            size: 12,
            weight: 600
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        borderColor: '#667eea',
        borderWidth: 1
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    }
  };

  return (
    <div className="table-component">
      <div className="button-group">
        <button onClick={handleToggle}>
          {showChart ? '📊 Show Table' : '📈 Show Chart'}
        </button>
        <button onClick={handleToggleWeighting}>
          {isWeighted ? '⚖️ Show Unweighted' : '⚖️ Show Weighted'}
        </button>
        <button onClick={handleToggleSigtest}>
          {isSigtest ? '🔬 Hide Sig Test' : '🔬 Show Sig Test'}
        </button>
        <button onClick={showChart ? downloadPPT : downloadExcel}>
          {showChart ? '💾 Download PPT' : '💾 Download Excel'}
        </button>
      </div>

      {showChart ? (
        <div className="chart-container" style={{ height: '500px' }}>
          <Bar data={chartData} options={chartOptions} />
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: "250px" }}></th>
                {cols && cols.map(col => (
                  <th key={col.index} style={{ maxWidth: "80px" }}>
                    {col.label}
                    {col.letter && (<div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '4px' }}>
                      [{col.letter}]
                    </div>)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {summary && (
                <>
                <tr style={{ background: '#FFFACD', fontWeight: 'bold' }}>
                  <td >
                    Base Unweighted
                  </td>
                    {cols && cols.map((col, colIdx) => {
                        const item = datavalue.find(
                          d => d[lvl1] === col.index &&
                               d[lvl2] === "1" &&
                               d[lvl3] === -98 &&
                               d[lvl4] === flt4
                        );
                        return (
                          <td key={`freq-${col.index}-${colIdx}`}>
                      {fmtFreq(item?.data?.base)}
                          </td>
                        );
                      })}
                </tr>
                <tr style={{ background: '#FFFACD', fontWeight: 'bold' }}>
                  <td >
                    Base Weighted
                  </td>
                    {cols && cols.map((col, colIdx) => {
                        const item = datavalue.find(
                          d => d[lvl1] === col.index &&
                               d[lvl2] === "1" &&
                               d[lvl3] === -99 &&
                               d[lvl4] === flt4
                        );
                        return (
                          <td key={`freq-${col.index}-${colIdx}`}>
                            {fmtFreq(item?.data?.base)}
                          </td>
                        );
                      })}
                </tr>
                </>
              )}
              {statements && (() => {
                const firstCol = cols?.[0];
                const visibleStatements = (szr && firstCol)
                  ? statements.filter(st => {
                      if (st.index < 0) return true; // always keep base, net, stat rows
                      const item = datavalue.find(
                        d => d[lvl1] === firstCol.index &&
                             d[lvl2] === st.index &&
                             d[lvl3] === flt3 &&
                             d[lvl4] === flt4
                      );
                      const freq = parseFloat(item?.data?.freq);
                      return isNaN(freq) || freq !== 0;
                    })
                  : statements;
                return visibleStatements.map((st, stIdx) => {
                const isUwBase = st.index === -98;
                const isWtBase = st.index === -99;
                const isBase   = isUwBase || isWtBase;
                const isStat   = st.index < -100 && st.index >= -200;

                // Respect base visibility settings
                if (isUwBase && !showUnweightedBase) return null;
                if (isWtBase && (!isWeighted || !showWeightedBase)) return null;

                return (
                <React.Fragment key={`statement-${st.index}-${stIdx}`}>
                  {displaySettings && displaySettings.showFrequency && (
                    <tr style={isBase ? { background: '#FFFACD', fontWeight: 'bold' } : {}}>
                      <td style={st.indent ? { paddingLeft: `${st.indent * 20 + 8}px` } : {}}>
                        {(st.label != null && st.label !== '') ? (st.index <= -5555 ? `NET: ${st.label}` : st.label) : ''}

                      </td>
                      {cols && cols.map((col, colIdx) => {
                        const item = datavalue.find(
                          d => d[lvl1] === col.index &&
                               d[lvl2] === st.index &&
                               d[lvl3] === flt3 &&
                               d[lvl4] === flt4
                        );
                        const _cSigF = (!isBase && !isStat) ? item?.data?.complimentSig : null
                        const _cDirF = _cSigF ? (isWeighted ? _cSigF.directionWt : _cSigF.direction) : ''
                        const _bgF = _cDirF === 'higher' ? {backgroundColor: '#d4f5d4'}
                                   : _cDirF === 'lower'  ? {backgroundColor: '#ffd6d6'}
                                   : {}
                        return (
                          <td key={`freq-${col.index}-${colIdx}`} style={_bgF}>
                            {isStat ? (() => {
                              if (!item) return '-';
                              const keys = STAT_KEYS[String(st.index)] || ['Mean', 'WeightedMean'];
                              const val = isWeighted ? item.data[keys[1]] : item.data[keys[0]];
                              return fmtStat(val !== undefined ? val : item.data[keys[0]], st.index);
                            })() : isWtBase ? (
                              item ? fmtFreq(item.data.freqwt) : '-'
                            ) : (
                              item ? (st.index === -98? fmtFreq(item.data.freq): fmtFreq(isWeighted ? item.data.freqwt : item.data.freq)) : '-'
                            )}

                          </td>
                        );
                      })}
     
                    </tr>
                  )}

                  {!isBase && !isStat && displaySettings && displaySettings.showPercent && (
                    <tr>
                      <td style={st.indent && !displaySettings.showFrequency ? { paddingLeft: `${st.indent * 20 + 8}px` } : {}}>
                        {(st.label != null && st.label !== '') && displaySettings && !displaySettings.showFrequency ? (st.index <= -5555 ? `NET: ${st.label}` : st.label) : ''}
                      </td>
                      {cols && cols.map((col, colIdx) => {
                        const item = datavalue.find(
                          d => d[lvl1] === col.index &&
                               d[lvl2] === st.index &&
                               d[lvl3] === flt3 &&
                               d[lvl4] === flt4
                        );
                        const _cSigP = item?.data?.complimentSig
                        const _cDirP = _cSigP ? (isWeighted ? _cSigP.directionWt : _cSigP.direction) : ''
                        const _bgP = _cDirP === 'higher' ? {backgroundColor: '#d4f5d4'}
                                   : _cDirP === 'lower'  ? {backgroundColor: '#ffd6d6'}
                                   : {}
                        return (
                          <td key={`perc-${col.index}-${colIdx}`} style={_bgP}>
                            {item ? fmtPct(isWeighted ? item.data.percwt : item.data.perc) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  )}

                  {!isBase && displaySettings && isSigtest && (
                    <tr style={{ background: 'rgba(102, 126, 234, 0.03)' }}>
                      <td style={{ fontStyle: 'italic', color: '#667eea', fontSize: '0.813rem', ...(st.indent && !displaySettings.showFrequency && !displaySettings.showPercent ? { paddingLeft: `${st.indent * 20 + 8}px` } : {}) }}>
                        {displaySettings.showFrequency || displaySettings.showPercent ? 'Sig Test' : (st.index <= -5555 ? `NET: ${st.label}` : st.label)}
                      </td>
                      {cols && cols.map((col, colIdx) => {
                        const item = datavalue.find(
                          d => d[lvl1] === col.index &&
                               d[lvl2] === st.index &&
                               d[lvl3] === flt3 &&
                               d[lvl4] === flt4
                        );
                        return (
                          <td
                            key={`sig-${col.index}-${colIdx}`}
                            style={{
                              fontWeight: '700',
                              color: '#764ba2',
                              fontSize: '0.813rem',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {(() => {
                              const colSyms = item?.data?.sig
                                ? (isWeighted ? item.data.sig.map(s => s.colSymbolWt).join('')
                                             : item.data.sig.map(s => s.colSymbol).join(''))
                                : ''
                              const cSig = item?.data?.complimentSig
                              const cSym = cSig ? (isWeighted ? (cSig.symbolWt || '') : (cSig.symbol || '')) : ''
                              return [colSyms, cSym].filter(Boolean).join(' ') || ''
                            })()}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </React.Fragment>
                );
              });
              })()}
            </tbody>
          </table>
        </div>
      )}
      {/* {JSON.stringify(datavalue)} */}
    </div>
  );
}

export default TableComponent;